/**
 * Home — role-tailored landing.
 *
 * Crew are sent straight to /today. PMs land on their own requests with a
 * "new request" CTA; sales on recently completed projects (the recap is the
 * hero artifact); supervisors/admins on the approvals + delivery-health
 * console. No /health ping or design-system showcase as primary content
 * (Pillar F, zero-stub DoD).
 */
import { Link, Navigate } from 'react-router-dom';
import {
  Activity,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FileText,
  ListChecks,
  Loader2,
  Package,
  Plus,
  RefreshCw,
  TriangleAlert,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { useProjects, useRequestReviews } from '@/api/projects';
import { usePendingTasks } from '@/api/tasks';
import { useOpsHealth, useRetryOutbox } from '@/api/ops';
import { ApproveScheduleButton } from '@/components/projects/ApproveScheduleButton';
import { ConfirmTaskButton } from '@/components/projects/ConfirmTaskButton';
import { ResubmitButton } from '@/components/projects/ResubmitButton';
import { useActivityFeed } from '@/api/activity';
import { ActivityFeedList } from '@/components/ActivityFeedList';
import { formatDateOnly, formatDateTime } from '@/lib/format';

export function Home() {
  const { data: me, isLoading } = useCurrentUser();

  if (isLoading || !me) {
    return (
      <div className="h-full grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }

  // Crew live in the mobile Today view — there's no desktop landing for them.
  if (me.role === 'crew') return <Navigate to="/today" replace />;

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Hi, {me.display_name.split(' ')[0]}.</h1>
          <p className="text-ink-dim">{roleBlurb(me.role)}</p>
        </div>
        {me.role === 'pm' && (
          <Button asChild>
            <Link to="/projects/new">
              <Plus className="w-4 h-4" /> New request
            </Link>
          </Button>
        )}
      </header>

      {me.role === 'pm' && <MyRequestsCard pmId={me.id} />}
      {me.role === 'sales' && <RecentCompletedCard />}
      {(me.role === 'supervisor' || me.role === 'admin') && (
        <>
          <PendingRequestsCard />
          <UpcomingWorkCard />
          <PendingTaskApprovalsCard />
          <OpsHealthCard />
          <RecentActivityCard />
        </>
      )}
    </div>
  );
}

/**
 * Distinct error state for the landing cards. Without it a failed fetch
 * renders the empty state — "No pending requests" while the API is down is
 * exactly the false-negative the approval queue exists to prevent.
 */
function CardError({ error, what }: { error: Error | null; what: string }) {
  if (!error) return null;
  return (
    <div className="flex items-center gap-2 text-sm text-status-blocked-fg" role="alert">
      <TriangleAlert className="w-4 h-4 shrink-0" />
      <span>
        Couldn&apos;t load {what} ({error.message}) — refresh to retry.
      </span>
    </div>
  );
}

/**
 * Cross-project field activity for supervisors/admins — the latest few, with a
 * link to the full feed. Each entry deep-links to its project + task.
 */
function RecentActivityCard() {
  const { data, isLoading, error } = useActivityFeed(8);
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-4 h-4" /> Recent activity
          </CardTitle>
          <CardDescription>
            Latest notes, photos, and punch items across all projects.
          </CardDescription>
        </div>
        <Link
          to="/activity"
          className="text-sm text-accent hover:underline shrink-0 whitespace-nowrap"
        >
          View all →
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-ink-dim">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        <CardError error={error as Error | null} what="activity" />
        {data && data.items.length === 0 && (
          <div className="text-sm text-ink-dim">
            No field activity yet — notes, photos, and punch items from the crew show up here.
          </div>
        )}
        {data && data.items.length > 0 && <ActivityFeedList items={data.items} />}
      </CardContent>
    </Card>
  );
}

function roleBlurb(role: string): string {
  switch (role) {
    case 'pm':
      return 'Track your project requests from submission through closeout.';
    case 'sales':
      return 'Recently completed installs and their recaps.';
    case 'admin':
      return 'Approvals and delivery health across the crew.';
    default:
      return 'Approvals and today’s delivery health.';
  }
}

// ---- PM: my requests -------------------------------------------------------

function MyRequestsCard({ pmId }: { pmId: string }) {
  const { data, isLoading, error } = useProjects({ pm_user_id: pmId, limit: 50, offset: 0 });
  const projects = data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4" /> My requests
          {projects.length > 0 && (
            <span className="ml-auto text-xs text-ink-faint font-normal">
              {projects.length} total
            </span>
          )}
        </CardTitle>
        <CardDescription>Your project requests and where each one stands.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-ink-dim">Loading…</div>}
        <CardError error={error} what="your requests" />
        {!isLoading && !error && projects.length === 0 && (
          <div className="text-sm text-ink-dim">
            No requests yet.{' '}
            <Link to="/projects/new" className="text-accent font-medium">
              Submit your first one →
            </Link>
          </div>
        )}
        {projects.map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between gap-3 py-2.5 border-b border-line last:border-0"
          >
            <div className="min-w-0">
              <Link
                to={`/projects/${p.id}`}
                className="font-medium text-sm hover:text-accent truncate block"
              >
                {p.title}
              </Link>
              <div className="text-xs text-ink-faint">{p.client_name}</div>
              {p.revision_requested_at && (
                <div className="text-[11px] text-amber-700 mt-0.5 line-clamp-2">
                  Needs revision{p.revision_note ? `: ${p.revision_note}` : ''}
                </div>
              )}
            </div>
            {p.revision_requested_at ? (
              <ResubmitButton project={p} />
            ) : (
              <StatusBadge status={p.status} />
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ---- Sales: recently completed --------------------------------------------

function RecentCompletedCard() {
  const { data, isLoading, error } = useProjects({ status: 'complete', limit: 15, offset: 0 });
  const projects = data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Recently completed
        </CardTitle>
        <CardDescription>Finished installs — open one for its recap.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-ink-dim">Loading…</div>}
        <CardError error={error} what="completed projects" />
        {!isLoading && !error && projects.length === 0 && (
          <div className="text-sm text-ink-dim">No completed projects yet.</div>
        )}
        {projects.map((p) => (
          <Link
            key={p.id}
            to={`/projects/${p.id}`}
            className="flex items-center justify-between gap-3 py-2.5 border-b border-line last:border-0 hover:text-accent"
          >
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{p.title}</div>
              <div className="text-xs text-ink-faint">{p.client_name}</div>
            </div>
            <StatusBadge status={p.status} />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

// ---- Supervisor / admin: approvals + delivery health ----------------------

const usd0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const money0 = (cents: number) => usd0.format(cents / 100);

function Signal({
  tone,
  icon: Icon,
  children,
}: {
  tone: 'danger' | 'warn' | 'muted';
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'danger'
      ? 'border-rose-200 bg-rose-50 text-rose-800'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-line bg-fill-soft text-ink-dim';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] ${cls}`}
    >
      <Icon className="w-3 h-3 shrink-0" />
      {children}
    </span>
  );
}

/** The project's committed start (first approved day) as an ISO date, or null. */
function committedStart(schedule: { date: string; start_time: string }[] | null): string | null {
  const days = (schedule ?? []).filter((d) => d?.date);
  if (days.length === 0) return null;
  const first = [...days].sort((a, b) => a.date.localeCompare(b.date))[0]!;
  return `${first.date}T${first.start_time ?? '00:00'}:00`;
}

/**
 * Upcoming / scheduled work (FMS feedback 2026-08-11, recap #7). Leo's landing
 * was an approvals console with no schedule view — he had to drill Projects →
 * task → start. This is the crew-style glance at what's committed and coming up,
 * sorted by committed start, so he can jump straight into a project.
 */
function UpcomingWorkCard() {
  // Projects are directly schedulable now (D-3), so "work" is the committed
  // project itself. Pull a page and filter client-side to committed, active work.
  const { data, isLoading, error } = useProjects({ limit: 50, offset: 0 });
  const ACTIVE = new Set(['scheduled', 'en_route', 'on_site', 'punch', 'pending_closeout']);
  const rows = (data?.data ?? [])
    .map((p) => ({ p, start: committedStart(p.approved_schedule) }))
    .filter(({ p }) => ACTIVE.has(p.status))
    // Committed (has a start) sorts first by that start; unscheduled-but-active last.
    .sort((a, b) => {
      if (a.start && b.start) return a.start.localeCompare(b.start);
      if (a.start) return -1;
      if (b.start) return 1;
      return 0;
    })
    .slice(0, 6);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4" />
          Upcoming work
        </CardTitle>
        <CardDescription>
          Committed projects, soonest first — open one to assign crew or start it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-ink-dim">Loading…</div>}
        <CardError error={error} what="the schedule" />
        {!isLoading && !error && rows.length === 0 && (
          <div className="text-sm text-ink-dim">No committed work right now.</div>
        )}
        <div className="space-y-2.5">
          {rows.map(({ p, start }) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}`}
              className="flex items-start justify-between gap-3 rounded-lg border border-line p-3 hover:border-ink-faint"
            >
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{p.title}</div>
                <div className="text-xs text-ink-faint truncate">
                  {p.client_name}
                  {start ? ` · ${formatDateTime(start)}` : ' · not scheduled yet'}
                </div>
              </div>
              <StatusBadge status={p.status} />
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PendingRequestsCard() {
  const { data, isLoading, error } = useRequestReviews();
  const reqs = data?.data ?? [];
  const count = reqs.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4" />
          Pending requests
          {count > 0 && (
            <span className="ml-auto inline-flex items-center justify-center rounded-full bg-status-requested text-status-requested-fg text-xs font-semibold w-6 h-6">
              {count}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          PM requests awaiting approval — check the estimate, back-orders, and
          site flags before approving.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-ink-dim">Loading…</div>}
        <CardError error={error} what="the approval queue" />
        {!isLoading && !error && count === 0 && (
          <div className="text-sm text-ink-dim">No pending requests.</div>
        )}
        <div className="space-y-2.5">
          {reqs.map((r) => (
            <div key={r.id} className="rounded-lg border border-line p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/projects/${r.id}`}
                    className="font-medium text-sm hover:text-accent truncate block"
                  >
                    {r.title}
                  </Link>
                  <div className="text-xs text-ink-faint truncate">
                    {r.client_name}
                    {r.pm_display_name ? ` · ${r.pm_display_name}` : ''}
                    {r.origin_warehouse?.length
                      ? ` · from ${r.origin_warehouse.join(', ')}`
                      : ''}
                  </div>
                </div>
                <ApproveScheduleButton
                  project={{
                    id: r.id,
                    title: r.title,
                    requested_start_date: r.requested_start_date,
                    requested_end_date: r.requested_end_date,
                    requested_schedule: r.requested_schedule,
                  }}
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.requested_start_date && (
                  <Signal tone="muted" icon={CalendarClock}>
                    wants {formatDateOnly(r.requested_start_date)}
                  </Signal>
                )}
                {r.labor ? (
                  <Signal tone={r.labor.over_budget ? 'danger' : 'muted'} icon={Clock}>
                    {r.labor.total_person_hours}h ·{' '}
                    {money0(r.labor.estimated_labor_cents)} labor
                    {r.labor.budget_cents != null &&
                      ` / ${money0(r.labor.budget_cents)}`}
                    {r.labor.over_budget && ' · over budget'}
                  </Signal>
                ) : r.delivery_install_budget_cents != null ? (
                  <Signal tone="muted" icon={Clock}>
                    {money0(r.delivery_install_budget_cents)} budget · no tasks yet
                  </Signal>
                ) : (
                  <Signal tone="muted" icon={Clock}>
                    No estimate yet
                  </Signal>
                )}
                {r.back_order_count > 0 && (
                  <Signal tone="danger" icon={Package}>
                    {r.back_order_count} back-ordered
                  </Signal>
                )}
                {r.readiness_flag_count > 0 && (
                  <Signal tone="warn" icon={TriangleAlert}>
                    {r.readiness_flag_count} site flag
                    {r.readiness_flag_count > 1 ? 's' : ''}
                  </Signal>
                )}
                <Signal tone="muted" icon={FileText}>
                  {r.document_count} doc{r.document_count === 1 ? '' : 's'}
                </Signal>
                <Signal tone="muted" icon={ListChecks}>
                  {r.task_count} task{r.task_count === 1 ? '' : 's'}
                </Signal>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PendingTaskApprovalsCard() {
  const { data, isLoading, error } = usePendingTasks();
  const tasks = data?.data ?? [];
  const count = tasks.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" />
          Requested tasks
          {count > 0 && (
            <span className="ml-auto inline-flex items-center justify-center rounded-full bg-status-requested text-status-requested-fg text-xs font-semibold w-6 h-6">
              {count}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Tasks a PM requested on an existing project — confirm to schedule the work.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-ink-dim">Loading…</div>}
        <CardError error={error as Error | null} what="requested tasks" />
        {!isLoading && !error && count === 0 && (
          <div className="text-sm text-ink-dim">No requested tasks.</div>
        )}
        <div className="space-y-2.5">
          {tasks.map((t) => (
            <div key={t.id} className="rounded-lg border border-line p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/projects/${t.project_id}`}
                    className="font-medium text-sm hover:text-accent truncate block"
                  >
                    {t.title}
                  </Link>
                  <div className="text-xs text-ink-faint truncate">
                    {t.project_title}
                    {t.client_name ? ` · ${t.client_name}` : ''}
                    {t.requested_by ? ` · ${t.requested_by}` : ''}
                  </div>
                </div>
                <ConfirmTaskButton
                  task={{
                    id: t.id,
                    title: t.title,
                    scheduled_start: t.scheduled_start,
                    scheduled_end: t.scheduled_end,
                    request_note: t.request_note,
                    crew_size_needed: t.crew_size_needed,
                    projectId: t.project_id,
                  }}
                />
              </div>
              {t.request_note && (
                <p className="mt-1.5 text-xs text-ink-dim italic line-clamp-2">“{t.request_note}”</p>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Signal tone="muted" icon={ListChecks}>
                  {t.type}
                </Signal>
                {t.scheduled_start ? (
                  <Signal tone="muted" icon={CalendarClock}>
                    {formatDateTime(t.scheduled_start)}
                  </Signal>
                ) : (
                  <Signal tone="warn" icon={CalendarClock}>
                    no time yet
                  </Signal>
                )}
                <Signal tone="muted" icon={Users}>
                  {t.crew_size_needed} crew
                </Signal>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OpsHealthCard() {
  const { data, isLoading, error } = useOpsHealth();
  const retry = useRetryOutbox();
  const failed = (data?.outbox_failed ?? 0) + (data?.notifications_failed ?? 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Delivery health
          {failed > 0 && (
            <span className="ml-auto inline-flex items-center justify-center rounded-full bg-status-blocked text-status-blocked-fg text-xs font-semibold w-6 h-6">
              {failed}
            </span>
          )}
        </CardTitle>
        <CardDescription>Notification + queued-update delivery status.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-ink-dim">Loading…</div>}
        <CardError error={error} what="delivery health" />
        {data && !error && failed === 0 && (
          <div className="text-sm text-ink-dim">All updates delivering normally.</div>
        )}
        {data && failed > 0 && (
          <div className="space-y-2 text-sm">
            {data.notifications_failed > 0 && (
              <div className="text-status-blocked-fg">
                {data.notifications_failed} notification
                {data.notifications_failed === 1 ? '' : 's'} failed to send
              </div>
            )}
            {data.outbox_failed > 0 && (
              <div className="text-status-blocked-fg">
                {data.outbox_failed} queued update{data.outbox_failed === 1 ? '' : 's'} stuck
              </div>
            )}
            {data.outbox_failed > 0 && (
              <Button
                size="sm"
                variant="outline"
                disabled={retry.isPending}
                onClick={() =>
                  retry.mutate(undefined, {
                    onSuccess: (r) =>
                      toast.success(
                        `Retrying ${r.retried} stuck update${r.retried === 1 ? '' : 's'}`,
                      ),
                    onError: (e) => toast.error(e.message),
                  })
                }
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry stuck updates
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
